<template>
    <div class="order-summary" :class="countryClass">
        <div class="order-summary__two-col">
            <template v-if="isPaymentPage">
                <h4 class="order-summary__heading">
                    {{ pageAttr.aa_order_summary_title }}
                </h4>
                <div class="order-summary__plan-link">
                    <router-link
                        :to="getSignupRouterLink"
                        v-if="displayChangePlanLink"
                        class="qt-changelink">
                        <span v-on:click="handleChangePlanLink">
                            {{ pageAttr.aa_order_summary_change_plan_cta }}
                        </span>
                    </router-link>
                </div>
            </template>

            <div class="col" v-html="planLabel"></div>
            <div class="col qt-payment-price">
                {{ this.internationalizedPriceWithUnit }}
                <span v-if="featureAsterisk && !isExSubscriber">*</span>
            </div>

            <template v-if="displayTrialInfo">
                <div class="col">
                    {{ pageAttr.aa_order_summary_trial_label }}
                </div>
                <div class="col qt-trial-price">
                    {{ coupon.trialString || plan.trialString }}
                </div>
            </template>

            <template v-if="coupon.discountString">
                <div class="col">{{ $getLocale('discount') }}</div>
                <div class="col qt-discount-price">
                    {{ coupon.discountString }}
                </div>
            </template>
        </div>
        <div v-if="isUpgradeAnnualToggle" class="upgrade-annual-toggle">
            <UpgradeAnnualToggle
                :plan-type="plan.planType"
                :attachedCoupon="coupon"
                @filterAndSelect="togglePlanType"></UpgradeAnnualToggle>
        </div>
        <div :class="summaryClassList">
            <div class="col col--row-total">
                {{ pageAttr.aa_order_summary_total_label }}
            </div>
            <div class="col col--row-total qt-total-price">
                <span v-show="showSubTotalNow" data-recurly="subtotal_now"></span>
                <span v-show="showSubTotalNext" data-recurly="subtotal_next"></span>
                <span v-show="showIntlZeroPrice">{{ this.internationalizedZeroPrice }}</span>
            </div>
        </div>

        <input type="hidden" data-recurly="plan" :value="plan.recurlyCode" />
        <p class="order-summary__disclaimer" v-html="disclaimerCopyEl"></p>
        <!-- START TEST PLOCTOPLUS-2572: A/B Test: Annual Upsell on Payment Page - Revised UI/UX -->
        <p class="order-summary__disclaimer" v-if="showAnnualUpgradeDisclaimer">
            {{ getAdditionalABTestDisclaimer }}
        </p>
        <!-- END TEST PLOCTOPLUS-2572: A/B Test: Annual Upsell on Payment Page - Revised UI/UX -->

        <p
            class="order-summary__dynamic-disclaimer"
            v-if="getDynamicDisclaimerCopy"
            v-html="getDynamicDisclaimerCopy"></p>

        <template v-if="isPaymentPage">
            <button
                class="order-summary__coupon-prompt"
                :aria-expanded="couponFieldOpenState ? 'true' : 'false'"
                @click="handleClickCouponPrompt">
                <div class="prompt qt-promo-link" tabindex="0">
                    {{ pageAttr.aa_coupon_field_prompt }}
                </div>
                <div :class="iconCaretClassList"></div>
            </button>

            <transition name="slide-down-fade">
                <div
                    v-if="couponFieldOpenState || !$store.state.promo || autoPromoChecked"
                    v-show="couponFieldOpenState">
                    <div class="order-summary__coupon">
                        <InputField
                            :key="getCouponCodeValue"
                            :inputConfig="getCouponInputConfig"
                            v-model="form['couponCode']"
                            :class="{ error: coupon.errorMessage }"
                            :keydownHandler="handleEnterKey"></InputField>
                        <InputElm
                            v-show="displayPromoField"
                            ref="planRecurly"
                            :inputConfig="orderSummaryConfig.hiddenInputList.plan" />
                        <input ref="couponRecurly" value="" type="hidden" data-recurly="coupon" />

                        <div class="order-summary__coupon__error qt-err-coupon-code">
                            {{ coupon.errorMessage }}
                        </div>

                        <ButtonCta :buttonConfig="buttonConfig"></ButtonCta>
                    </div>
                </div>
            </transition>
        </template>
    </div>
</template>

<script>
    import { mapState, mapGetters, mapActions } from 'vuex';
    import { catanAddOnCode } from 'aa/vue/constants/aaBundle';
    import InputElm from 'atoms/InputElm';
    import InputField from 'atoms/InputField';
    import pricePrettyPrint from 'aa/vue/plugins/priceMixin';
    import aarpMixin from 'aa/vue/plugins/aarpMixin.js';
    import sharedPurchaseMixin from 'aa/vue/plugins/account/sharedPurchaseMixin';
    import ButtonCta from 'atoms/ButtonCta';
    import { OrderSummaryConfig } from 'aa/vue/FormConfig';
    import WaitingOverlay from 'aa/vue/atoms/WaitingOverlay';
    import { PRICE_WITH_ASTERISK, MULTI_SUB_PLAN_PICKER_ENABLED } from 'helpers/featureConstants';
    import { DateFormatter, getEndDate } from 'aa/helpers/DateFormatter';
    import { sanitizer } from 'apps/plugins/Sanitizer';
    import Domain from 'helpers/Domain';
    import { tracking } from 'services/Tracking';
    import {
        PAGE_TYPES as AA_PAGE_TYPES,
        ACTION_NAMES,
    } from 'aa/vue/constants/aaTrackingConstants';
    // START TEST PLOCTOPLUS-2572: A/B Test: Annual Upsell on Payment Page - Revised UI/UX
    import UpgradeAnnualToggle from 'aa/vue/components/UpgradeAnnualToggle';
    import Product from 'aa/vue/models/Product';

    export default {
        components: {
            InputElm,
            InputField,
            ButtonCta,
            WaitingOverlay,
            UpgradeAnnualToggle,
        },

        props: {
            coupon: {
                type: Object,
            },
            couponDisclaimer: {
                type: String,
            },
            couponCheckPending: {
                type: Boolean,
            },
            recurly: {
                type: Object,
            },
            annualPlanOption: {
                type: Boolean,
                default: true,
            },
            formConfig: {
                type: Object,
                default: null,
            },
            isFromDirect: {
                type: Boolean,
                default: false,
            },
        },

        mixins: [pricePrettyPrint, aarpMixin, sharedPurchaseMixin],
        data: function () {
            return {
                form: {},
                countryClass: '',
                couponFieldManualOpen: null,
                couponFieldOpenState: null,
                internationalizedPrice: null,
                internationalizedZeroPrice: null,
                internationalizedPriceWithUnit: null,
                orderSummaryConfigDefault: OrderSummaryConfig,
                // we need this flag on component level to maintain plan type locally,
                // if user change plan multiples times on payment page then we have to show/hide a/b test UI
                isUserOriginalPlanTypeMonthly: true,
            };
        },

        computed: {
            ...mapGetters('user', ['isNoTrialUser']),
            ...mapGetters(['bundleShowtime']),
            ...mapGetters({
                upgradeAnnualPlanTiles: 'featureFlags/upgradeAnnualPlanTiles',
                upgradeAnnualToggle: 'featureFlags/upgradeAnnualToggle',
                selectedPlan: 'plan/getSelectedPlan',
            }),
            ...mapState('pageAttributes', {
                pageAttr: (state) => state.attributes,
            }),
            ...mapState('user', {
                isExSubscriber: (state) => state.isExSubscriber,
            }),
            ...mapState(['promo', 'autoPromoChecked']),
            orderSummaryConfig() {
                return this.formConfig || this.orderSummaryConfigDefault;
            },
            exSubWithTrial() {
                return this.isExSubscriber && !!this.coupon.trialString;
            },
            isUpgradeAnnualPlanTiles() {
                return this.upgradeAnnualPlanTiles && this.isUserOriginalPlanTypeMonthly;
            },
            //END TEST PLOCTOPLUS-2572: A/B Test: Annual Upsell on Payment Page - Revised UI/UX
            isUpgradeAnnualToggle() {
                return (
                    this.upgradeAnnualToggle &&
                    !this.pageAttr?.aa_payment_upgrade_to_annual_hide &&
                    this.isUserOriginalPlanTypeMonthly &&
                    (this.canToggleFromMonthlyToAnnual || this.canToggleFromAnnualToMonthly)
                );
            },

            getSignupRouterLink() {
                var baseLink = '/account/signup/pickplan/';
                var bundleLink = `${baseLink}bundle/${catanAddOnCode().toLowerCase()}/`;

                return this.$store.getters['isPartnerBundle'] ? bundleLink : baseLink;
            },

            displayTrialInfo() {
                if (this.removeTrial) {
                    return false;
                }
                return this.coupon?.trialString || this.plan?.trialString;
            },

            removeTrial() {
                // ex-subs without trial extension or coupon not eligible for free trial
                return (
                    Domain.isDomestic() &&
                    ((this.isNoTrialUser && !this.coupon.trialString) || !this.plan.trial)
                );
            },

            isInternational() {
                return Domain.isInternational();
            },

            isNoTrialUserWithCouponTrial() {
                return this.isNoTrialUser && !!this.coupon.trialString;
            },

            isNoTrialUserWithoutCouponTrial() {
                return this.isNoTrialUser && !this.coupon.trialString;
            },

            hasPromoCode() {
                return this.$store.state.promo == null ? false : true;
            },

            promoSuppressedByShowtimeBundleCopy() {
                if (this.pageAttr.aa_promo_suppressed_message_showtime === undefined) {
                    return '';
                } else {
                    return this.pageAttr.aa_promo_suppressed_message_showtime.replace(
                        '%COUPON_CODE%',
                        this.$store.state.promo,
                    );
                }
            },

            plan() {
                const trialString =
                    this.selectedPlan?.trialString && this.isNoTrialUser
                        ? null
                        : this.selectedPlan.trialString;

                return {
                    ...this.selectedPlan,
                    trialString,
                };
            },

            planLabel() {
                if (Domain.isInternational()) {
                    const planLabel = this.$store.getters.featureIsActive(
                        MULTI_SUB_PLAN_PICKER_ENABLED,
                    )
                        ? this.$store.getters['plan/getSelectedPlanLabelKey']
                        : this.$store.getters['plan/getPlanLabelKey'];

                    return this.$getLocale(planLabel);
                }

                return this.plan.planTitle;
            },

            displayChangePlanLink() {
                return (
                    this.$store.getters['plan/getPlans'].length > 1 &&
                    !this.$store.getters['flow/isEdu']
                );
            },

            iconCaretClassList() {
                return ['icon', 'icon--caret', this.couponFieldOpenState ? 'icon--caret--up' : ''];
            },

            summaryClassList() {
                const classList = ['order-summary__two-col'];

                return classList;
            },

            couponTrialEndDate() {
                let discount = !this.coupon.recurlyInfo ? null : this.coupon.recurlyInfo.discount;

                if (!discount || discount.type !== 'free_trial') {
                    return null;
                }

                try {
                    let { amount, unit } = discount.trial;
                    let trialEndDate = getEndDate(amount, unit);
                    return DateFormatter.format(trialEndDate, null, CBS.Registry.region.dateLocale);
                } catch (e) {
                    return null;
                }
            },

            trialEndDate() {
                const trialEndDate = new Date(this.plan.trialEndDate);
                const trialEndDateFormatted =
                    this.couponTrialEndDate ||
                    DateFormatter.format(trialEndDate, null, CBS.Registry.region.dateLocale);
                this.$emit('trialEndDateFormatted', trialEndDateFormatted);
                return trialEndDateFormatted;
            },

            trialEndCopy() {
                if (!this.plan.trialUnit || this.removeTrial || this.couponCheckPending) {
                    return '';
                }

                let trialEndEl = `<span id="trial-end-date" data-original="${this.plan.trialEndDate}">
                                        ${this.trialEndDate}</span>`;

                return this.pageAttr.aa_order_summary_trial_end_copy.replace('%d', trialEndEl);
            },
            getDynamicDisclaimerCopy() {
                return sanitizer(
                    this.couponDisclaimer
                        ? this.couponDisclaimer
                        : this.pageAttr.aa_campaign_upsell_disclaimer,
                );
            },

            disclaimerCopyEl() {
                return this.trialEndCopy + ' ' + this.pageAttr.aa_order_summary_tax_disclaimer;
            },

            // START TEST PLOCTOPLUS-2572: A/B Test: Annual Upsell on Payment Page - Revised UI/UX
            showAnnualUpgradeDisclaimer() {
                return this.isUpgradeAnnualToggle;
            },
            getAdditionalABTestDisclaimer() {
                return this.pageAttr?.aa_payment_upgrage_annual_additional_disclaimer ?? '';
            },
            // END TEST PLOCTOPLUS-2572: A/B Test: Annual Upsell on Payment Page - Revised UI/UX

            buttonConfig() {
                let buttonConfig = OrderSummaryConfig.buttonConfig;

                if (this.isAarpFlow()) {
                    buttonConfig.styleClass.push('disabled');
                }

                return Object.assign(
                    {
                        displayText: this?.pageAttr?.aa_coupon_field_cta ?? this.$getLocale('ok'),
                        event: this.handleClickCouponCTA,
                    },
                    buttonConfig,
                );
            },

            displayPromoField() {
                return this.promo && this.orderSummaryConfig.inputList.couponCode.value;
            },

            isFromDirectWithCouponString() {
                return this.isFromDirect && !!this.coupon.discountString;
            },

            showSubTotalNow() {
                return (
                    !this.removeTrial || this.isFromDirectWithCouponString || this.isInternational
                );
            },

            showSubTotalNext() {
                return (
                    this.removeTrial &&
                    !this.isNoTrialUserWithCouponTrial &&
                    !this.isFromDirectWithCouponString
                );
            },

            showIntlZeroPrice() {
                return this.removeTrial && this.isNoTrialUserWithCouponTrial;
            },
            currentPromoCode() {
                return this.coupon?.recurlyInfo?.code ?? '';
            },
            // Need trigger for component once coupon gets new value
            getCouponCodeValue() {
                return (
                    this.currentPromoCode ??
                    this.orderSummaryConfig?.inputList?.couponCode?.value ??
                    ''
                );
            },
            getCouponInputConfig() {
                return {
                    ...this.orderSummaryConfig?.inputList.couponCode,
                    value: this.getCouponCodeValue,
                };
            },
        },

        methods: {
            getInternationalizedPricesUsingRecurly() {
                if (this.recurly) {
                    const { currencySymbol, currencyCode } = this.recurly.Pricing.Checkout();
                    const selectedPlanPrice = parseFloat(this.plan?.rawPrice.replace(/,/g, ''));
                    this.internationalizedZeroPrice = `${this.pricePrettyPrint(0, currencySymbol, currencyCode)}`;
                    this.internationalizedPrice = `${this.pricePrettyPrint(
                        selectedPlanPrice,
                        currencySymbol,
                        currencyCode,
                    )}`;

                    this.internationalizedPriceWithUnit = `${this.pricePrettyPrint(
                        selectedPlanPrice,
                        currencySymbol,
                        currencyCode,
                    )}/${this.plan?.priceUnit}`;

                    this.$emit('found-price', {
                        internationalizedPrice: this.internationalizedPrice,
                        internationalizedPriceWithUnit: this.internationalizedPriceWithUnit,
                    });
                }
            },

            handleClickCouponPrompt(e) {
                e.preventDefault();
                if (this.couponFieldOpenState) {
                    this.manuallyCloseCouponField();
                } else {
                    this.manuallyOpenCouponField();
                }
            },
            setStartingStateOfCouponField() {
                this.couponFieldOpenState = false;
            },
            manuallyOpenCouponField() {
                this.couponFieldManualOpen = true;
                this.couponFieldOpenState = true;
            },

            manuallyCloseCouponField() {
                this.couponFieldManualOpen = false;
                this.couponFieldOpenState = false;
            },

            handleClickCouponCTA(e) {
                e.preventDefault();
                const couponCode = this.attachCurrentCouponToForm();
                if (this.form.couponCode?.value !== null && this.form.couponCode?.value !== '') {
                    this.$refs.couponRecurly.value = couponCode;
                } else {
                    //resetting coupon error message and css class
                    this.resetCouponFieldErrorMessage();
                }

                this.$emit('applyCoupon', {
                    plan: this.plan.recurlyCode,
                    couponCode: couponCode,
                });
            },

            handleEnterKey(e) {
                if (e.key === 'Enter' || e.code === 'Enter') {
                    e.stopPropagation();
                    e.preventDefault();

                    this.handleClickCouponCTA(e);
                }
            },

            attachCurrentCouponToForm(couponCodeOverride) {
                let couponCode = couponCodeOverride
                    ? couponCodeOverride
                    : this.form.couponCode?.value || '';

                if (this.$refs.couponRecurly) {
                    this.$refs.couponRecurly.value = couponCode;
                }

                return couponCode;
            },

            resetCouponFieldErrorMessage() {
                document.querySelector('.field-wrapper--coupon-code').classList.remove('error');
                this.$emit('resetCoupon');
            },

            handleChangePlanLink() {
                tracking.trackAction('trackChangeYourPlan', {
                    productOfferPeriod: this.plan?.trialString,
                    pageUrl: window.location.href,
                    pageType: 'svod_payment',
                    pickPlanType: this.planLabel,
                });
            },

            ...mapActions('plan', ['filterPlans', 'updateSelectedPlan']),
            // END TEST PLOCTOPLUS-2572: A/B Test: Annual Upsell on Payment Page - Revised UI/UX
            filterPlanAndUpdateSelected(action) {
                if (action) {
                    this.$store
                        .dispatch('plan/filterPlans', {
                            planType: Product.TYPE_ANNUAL,
                        })
                        .then(() => {
                            this.updateSelectedPlan(this.monthlySelectedAndAnnualAvailable?.[0]);
                        });
                } else {
                    this.$store
                        .dispatch('plan/filterPlans', {
                            planType: Product.TYPE_MONTHLY,
                        })
                        .then(() => {
                            this.updateSelectedPlan(this.annualSelectedAndMonthlyAvailable?.[0]);
                        });
                }

                const trackingAction =
                    this.isUpgradeAnnualToggle === true
                        ? ACTION_NAMES.PAYMENT_ANNUAL_PLAN_TOGGLE
                        : ACTION_NAMES.SELECT_NEW_BILLING_CADENCE;

                const trackingParams = {
                    pickPlanIsAnnual: action.toString(),
                    productOfferPeriod: this.plan?.trialString ?? '',
                    pickPlanSku: this.plan?.recurlyCode ?? '',
                    purchaseProductName: this.plan?.titleForTracking ?? '',
                };

                if (this.isExSubscriber) {
                    trackingParams.pageType = AA_PAGE_TYPES.EXSUB_PAYMENT;
                }

                tracking.trackAction(trackingAction, trackingParams);
            },
            couponIsValid(coupon) {
                if (coupon.errorMessage) {
                    return false;
                }

                return coupon?.discountString || coupon?.trialString || coupon?.recurlyInfo;
            },
            couponIsEmpty(coupon) {
                return (
                    !coupon?.discountString &&
                    !coupon?.trialString &&
                    !coupon?.recurlyInfo &&
                    !coupon?.errorMessage
                );
            },
        },

        // Lifecycle Hooks
        beforeCreate: function () {
            Object.assign(OrderSummaryConfig.hiddenInputList.plan, {
                value: this.selectedPlan?.recurlyCode,
            });
        },
        created() {
            this.setStartingStateOfCouponField();

            this.registrationCountry = this.$store.state.serverData.paymentForm?.country?.value
                ? this.$store.state.serverData.paymentForm?.country?.value.toLowerCase()
                : false;
            this.countryClass = `order-summary-${this.registrationCountry}`;
            this.featureAsterisk = this.$store.getters.featureIsActive(PRICE_WITH_ASTERISK);

            if (this.recurly) {
                this.getInternationalizedPricesUsingRecurly();
            }

            if (this.isAarpFlow()) {
                Object.assign(OrderSummaryConfig.inputList.couponCode, {
                    disabled: true,
                });
            }

            // we need isUserOriginalPlanTypeMonthly flag to maintain local state, to show a/b test UI if user change plan on payment page
            if (this.upgradeAnnualToggle) {
                this.isUserOriginalPlanTypeMonthly = this.plan.planType === Product.TYPE_MONTHLY;
            }
        },
        watch: {
            recurly(newVal, oldVal) {
                if (oldVal === null && newVal !== null) {
                    this.getInternationalizedPricesUsingRecurly();
                }
            },
            plan(newVal) {
                const couponCode = this.attachCurrentCouponToForm(this.plan?.offerCouponCode ?? '');

                this.$emit(
                    'updateOrder',
                    {
                        plan: this.plan.recurlyCode,
                        couponCode: couponCode,
                    },
                    false,
                );
                this.getInternationalizedPricesUsingRecurly();
            },
            autoPromoChecked(newVal) {
                if (newVal === true) {
                    this.$nextTick(() => {
                        this.attachCurrentCouponToForm(
                            this.orderSummaryConfig?.inputList?.couponCode?.value,
                        );
                    });
                }
            },
            coupon(newVal, oldVal) {
                const shouldCloseCouponForm =
                    this.couponIsValid(newVal) ||
                    // For when old coupon was valid and new coupon is empty -- we want to close / reset the form
                    (this.couponIsValid(oldVal) && this.couponIsEmpty(newVal));

                if (shouldCloseCouponForm) {
                    this.manuallyCloseCouponField();
                } else if (newVal?.errorMessage) {
                    this.manuallyOpenCouponField();
                }

                utag_data.productDiscount = newVal?.discountString;
                utag_data.promoOffer = localStorage.getItem('promoOffer');
            },
        },
    };
</script>
